import clsx from 'clsx'
import { rem } from 'polished'
import type { MouseEventHandler } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import IconButton from 'LuxKit/Button/IconButton'
import AssistChip from 'LuxKit/Chips/AssistChip'
import LineEditAltIcon from 'LuxKit/Icons/line/LineEditAltIcon'
import SolidArrowUpIcon from 'LuxKit/Icons/solid/SolidArrowUpIcon'
import Caption, { CaptionLarge } from 'LuxKit/Typography/Caption'
import InputText from 'LuxKit/Typography/InputText'
import Group from 'components/utils/Group'
import type { PatchTripItemError } from 'tripPlanner/api/tripItem/types'
import { usePatchTripItem, useDeleteTripItem } from 'tripPlanner/hooks/api/tripItem'
import { useTripPermissions } from 'tripPlanner/hooks/useTripPermissions'
import type { TripItem } from 'tripPlanner/types/tripItem'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'

const NotesBlock = styled(Group)`
  background-clip: border-box;
  grid-area: 1 / 1;
  z-index: 1;
  background-color: inherit;
`

const ConfirmButton = styled(IconButton)`
  display: none;

  &.focus {
    display: block;
  }
`

const ValidationMessage = styled(InputText)`
  position: absolute;
  bottom: ${rem(-4)};
  left: 0;
  padding-left: ${rem(8)};
  transform: translateY(100%);
`

const AutoGrowWrapper = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  position: relative;
  background-color: inherit;

  ${CaptionLarge}

  &.extraspace {
    margin-bottom: ${rem(20)};
  }

  &::after {
    /* Note the weird space! Needed to preventy jumpy behavior */
    content: attr(data-replicated-value) ' ';
    /* This is how textarea text behaves */
    white-space: pre-wrap;
    /* Hidden from view, clicks, and screen readers */
    visibility: hidden;
    padding: ${rem(4)} ${rem(38)} ${rem(4)} ${rem(4)};
  }

  textarea {
    margin: 0;
    resize: none;
    overflow: auto;
    width: 100%;
    height: 100%;
    background-color: inherit;
    font: inherit;
    &.error {
      border-color: ${(props) =>
        props.theme.palette.messaging.critical.normalForeground};
    }
    overflow-wrap: anywhere;
  }

  textarea,
  &::after {
    border: 1px solid rgba(0, 0, 0, 0);
    font: inherit;
    text-overflow: ellipsis;
    vertical-align: bottom;
    /* Place on top of each other */
    grid-area: 1 / 1 / 2 / 2;
    overflow-wrap: anywhere;
    max-height: ${rem(106)};
  }

  textarea::-webkit-scrollbar {
    display: none;
  }

  @media print {
    display: none;
  }
`

const ReadOnlyNotes = styled(Caption)`
  overflow-wrap: anywhere;
`

type Props = {
  item: TripItem
  inputAlwaysVisible?: boolean
  deleteItemOnClear?: boolean
  autoFocus?: boolean
  inFocus?: boolean
  onFocus?: () => void
  onBlur?: () => void
  onNotesChange?: (notes: string | undefined) => void
  className?: string
}

function TripItemInlineNoteEdit({
  item,
  inputAlwaysVisible = false,
  deleteItemOnClear = false,
  autoFocus = false,
  inFocus: externalInFocus,
  onFocus: externalOnFocus,
  onBlur: externalOnBlur,
  onNotesChange,
  className,
}: Props) {
  const intl = useIntl()
  const { canEdit } = useTripPermissions(item.tripId)
  const [notes, setNotes] = useState<string | undefined>(item.notes)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [internalInFocus, setInternalInFocus] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)

  const inFocus = externalInFocus !== undefined ? externalInFocus : internalInFocus
  const onTextInput = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setNotes(e.currentTarget.value)
      if (errorMsg) setErrorMsg('')
    },
    [errorMsg, setNotes],
  )

  useEffect(() => {
    // Reset notes if item notes changes
    setNotes(item.notes)
  }, [item.notes])

  const { mutate: patchTripItem, isLoading } = usePatchTripItem({
    onError: (e: PatchTripItemError) => {
      setErrorMsg(e.error)
      if (!notes) {
        setNotes(item.notes)
      }
    },
  })
  const { mutate: deleteTripItem, isLoading: isDeleting } = useDeleteTripItem()

  const onFocus = useCallback(() => {
    if (externalOnFocus) {
      externalOnFocus()
    } else {
      setInternalInFocus(true)
    }
  }, [externalOnFocus])

  const onBlur = useCallback(() => {
    if (externalOnBlur) {
      externalOnBlur()
    } else {
      setInternalInFocus(false)
    }

    if (isLoading) return

    /* UI optimistic update and revert the notes if BE update fails */
    if (onNotesChange && notes !== item.notes) {
      onNotesChange(notes)
    }

    if (deleteItemOnClear && !notes) {
      deleteTripItem({ tripId: item.tripId, tripItemId: item.id },
        {
          onError: () => {
            if (onNotesChange) {
              onNotesChange(item.notes)
            }
            showSnackbar(intl.formatMessage({
              id: 'fwJ_0HU_6daMA3YWdszv',
              defaultMessage: 'Failed to delete item! Please try again.',
              description: 'Error snackbar shown when deleting a trip item note fails',
            }), 'critical')
          },
        })
    } else if (notes !== item.notes) {
      patchTripItem(
        {
          tripId: item.tripId,
          tripItemId: item.id,
          tripItem: { type: item.type, notes, link: item.type === 'NOTE' ? null : undefined },
        },
        {
          onError: () => {
            if (onNotesChange) {
              onNotesChange(item.notes)
            }
            showSnackbar(intl.formatMessage({
              id: 'RE0pJesuzgTix6p7H_Ug',
              defaultMessage: 'Failed to update notes! Please try again.',
              description: 'Error snackbar shown when updating trip item notes fails',
            }), 'critical')
          },
        },
      )
    }
  }, [externalOnBlur, isLoading, notes, item, patchTripItem, deleteItemOnClear, deleteTripItem, onNotesChange, intl])

  const onEdit = useCallback<MouseEventHandler>(
    (e) => {
      if (externalOnFocus) {
        externalOnFocus()
      } else {
        setInternalInFocus(true)
      }
      if (inputRef.current) {
        inputRef.current.focus()
      }
      e.preventDefault()
    },
    [inputRef, externalOnFocus],
  )

  const onConfirmClick = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Blur on the next tick so the element isn't removed before the event sequence completes
    // (avoids retargeting to the underlying card on mobile browsers).
    inputRef.current?.blur()
  }, [])

  useEffect(() => {
    if (inFocus && inputRef.current) {
      inputRef.current.focus()
    }
  }, [inFocus])

  if (!canEdit) {
    return notes && notes.length > 1 ? (
      <ReadOnlyNotes variant="medium" wrap="pre-wrap">
        {notes}
      </ReadOnlyNotes>
    ) : null
  }

  if (!notes && !inFocus && !inputAlwaysVisible) {
    return (
      <AssistChip
        size="small"
        onClick={onEdit}
        variant="filled"
        startIcon={<LineEditAltIcon />}
      >
        {intl.formatMessage({
          id: 'VSSDpcYcgmOIlZedOwUl',
          defaultMessage: 'Note',
          description: 'Chip label to add a note to a trip item in the trip planner feature',
        })}
      </AssistChip>
    )
  }

  return (
    <AutoGrowWrapper
      data-replicated-value={notes}
      className={clsx({ extraspace: !!errorMsg, focus: inFocus }, className)}
    >
      <NotesBlock
        direction="horizontal"
        gap={0}
        verticalAlign="end"
        horizontalAlign="space-between"
        fullWidth
        className={clsx({ focus: inFocus })}
      >
        <textarea
          rows={1}
          onInput={onTextInput}
          onFocus={onFocus}
          onBlur={onBlur}
          value={notes}
          minLength={1}
          className={clsx({ error: !!errorMsg })}
          spellCheck={inFocus}
          ref={inputRef}
          disabled={isDeleting}
          autoFocus={autoFocus}
          placeholder={intl.formatMessage({
            id: 'xYxtD_rFdWAT9y1EtBKs',
            defaultMessage: 'Add a comment',
            description: 'Placeholder text for the trip item comment input field',
          })}
        />
        <ConfirmButton
          className={clsx({ focus: inFocus })}
          shape="square"
          kind="tertiary"
          variant="default"
          size="small"
          aria-label={intl.formatMessage({
            id: 'aBRsvWI43Rgv6HIvdWWA',
            defaultMessage: 'Update Notes',
            description: 'Aria label for button to confirm and save trip item notes',
          })}
          data-testid="trip-item-update-notes-button"
          type="button"
          onPointerDownCapture={onConfirmClick}
          onMouseDownCapture={onConfirmClick}
          onTouchStartCapture={onConfirmClick}
          onClick={onConfirmClick}
          disabled={!inFocus}
        >
          <SolidArrowUpIcon size={24} />
        </ConfirmButton>
      </NotesBlock>
      {!!errorMsg && (
        <ValidationMessage variant="caption" colour="critical">
          {errorMsg}
        </ValidationMessage>
      )}
    </AutoGrowWrapper>
  )
}

export default TripItemInlineNoteEdit
